// HTTP wrapper para a API AXIS Comprovantes
//
// Resolução da URL base da API (em ordem de prioridade):
//   1. window.__AXIS_COMPROVANTES_CONFIG__.apiBaseUrl  (runtime, projeto host)
//   2. import.meta.env.VITE_API_BASE_URL               (build time)
//   3. "" (mesma origem)
declare global {
  interface Window {
    __AXIS_COMPROVANTES_CONFIG__?: {
      apiBaseUrl?: string;
    };
  }
}

function resolveApiBaseUrl(): string {
  const runtime =
    typeof window !== "undefined"
      ? window.__AXIS_COMPROVANTES_CONFIG__?.apiBaseUrl
      : undefined;
  const buildTime = import.meta.env.VITE_API_BASE_URL as string | undefined;
  return (runtime ?? buildTime ?? "").replace(/\/$/, "");
}

export const API_BASE_URL = resolveApiBaseUrl();

const TOKEN_KEY = "axis_token";

export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null) {
  if (typeof window === "undefined") return;
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export class ApiError extends Error {
  status: number;
  payload: unknown;
  constructor(message: string, status: number, payload?: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  query?: Record<string, unknown>;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const params = new URLSearchParams();

  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;

      if (Array.isArray(value)) {
        for (const item of value) {
          if (item !== undefined && item !== null) {
            params.append(key, String(item));
          }
        }
      } else {
        params.set(key, String(value));
      }
    }
  }

  const qs = params.toString();

  return `${path.startsWith("/") ? "" : "/"}${path}${qs ? `?${qs}` : ""}`;
}

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", query, body, headers = {}, signal } = options;
  const token = getToken();
  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    ...headers,
  };
  let payload: BodyInit | undefined;
  if (body !== undefined && body !== null) {
    finalHeaders["Content-Type"] = finalHeaders["Content-Type"] ?? "application/json";
    payload = typeof body === "string" ? body : JSON.stringify(body);
  }
  if (token) finalHeaders["Authorization"] = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers: finalHeaders,
      body: payload,
      signal,
      // Autenticação por Cookie HttpOnly gerenciado pelo backend
      credentials: "include",
    });
  } catch (err) {
    throw new ApiError(
      err instanceof Error ? `Falha de rede: ${err.message}` : "Falha de rede",
      0,
    );
  }

  const text = await response.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!response.ok) {
    const message =
      (data && typeof data === "object" && "message" in (data as Record<string, unknown>)
        ? String((data as Record<string, unknown>).message)
        : null) ?? `Erro ${response.status} ao chamar ${path}`;
    throw new ApiError(message, response.status, data);
  }

  return data as T;
}

// Upload direto (PUT) usando link e cabeçalhos retornados pela API
export async function uploadToSignedUrl(
  url: string,
  file: File,
  opts: {
    headers?: Record<string, string>;
    onProgress?: (pct: number) => void;
    signal?: AbortSignal;
  } = {},
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    const headers = opts.headers ?? {};
    let hasContentType = false;
    for (const [k, v] of Object.entries(headers)) {
      if (k.toLowerCase() === "content-type") hasContentType = true;
      try {
        xhr.setRequestHeader(k, v);
      } catch {
        /* alguns headers são proibidos pelo browser */
      }
    }
    if (!hasContentType && file.type) xhr.setRequestHeader("Content-Type", file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && opts.onProgress) {
        opts.onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new ApiError(`Falha no upload (${xhr.status})`, xhr.status));
    };
    xhr.onerror = () => reject(new ApiError("Falha de rede no upload", 0));
    xhr.onabort = () => reject(new ApiError("Upload cancelado", 0));
    if (opts.signal) {
      opts.signal.addEventListener("abort", () => xhr.abort());
    }
    xhr.send(file);
  });
}
